import { useEffect, useState } from "react";

import { isTauri } from "./notifications/desktopBridge.js";

/**
 * Silent desktop app-version tracking (no popup) that powers the
 * "What's new since you upgraded" deep-link.
 *
 * The Tauri auto-updater installs in place and restarts, so after an update
 * the app just knows its *new* version. To offer "what changed since the
 * version you had", we persist the last-seen version in localStorage (which
 * survives the in-place update) and, the first launch on a new version,
 * record the version we upgraded FROM. That `upgradedFrom` sticks until the
 * next upgrade, so the link stays meaningful between updates.
 *
 * Desktop-only: on the web (`!isTauri()`) there is no app version, so this
 * returns nulls and the About card renders nothing.
 */
const LAST_SEEN_KEY = "xop:lastSeenAppVersion";
const UPGRADED_FROM_KEY = "xop:upgradedFromVersion";

/** localStorage access that never throws (private mode / disabled storage). */
function readLS(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLS(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — degrade gracefully (no persisted "what's new") */
  }
}

export interface AppVersionInfo {
  /** The running desktop app version, or null on web / when unavailable. */
  currentVersion: string | null;
  /** The version the user upgraded FROM (persists until the next upgrade). */
  upgradedFrom: string | null;
}

export function useAppVersion(): AppVersionInfo {
  const [info, setInfo] = useState<AppVersionInfo>({
    currentVersion: null,
    upgradedFrom: null,
  });

  useEffect(() => {
    if (!isTauri()) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const { getVersion } = await import("@tauri-apps/api/app");
        const current = await getVersion();
        if (cancelled) return;
        const lastSeen = readLS(LAST_SEEN_KEY);
        let upgradedFrom = readLS(UPGRADED_FROM_KEY);
        if (lastSeen && lastSeen !== current) {
          // First launch on a new version → remember where we came from.
          upgradedFrom = lastSeen;
          writeLS(UPGRADED_FROM_KEY, lastSeen);
        }
        writeLS(LAST_SEEN_KEY, current);
        setInfo({ currentVersion: current, upgradedFrom });
      } catch {
        /* version API unavailable (older shell) — leave nulls */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return info;
}

/**
 * Deep-link into the Change Log scoped to "since the user's previous
 * version", or the plain change log when there's no recorded upgrade.
 */
export function whatsNewHref(info: AppVersionInfo): string {
  return info.upgradedFrom
    ? `/admin/changelog?sinceVersion=${encodeURIComponent(info.upgradedFrom)}`
    : "/admin/changelog";
}
